/**
 * CoachPlaybook - Main Application Component (Mobile-First PWA)
 * Version: 0.6.0
 * Description: Mobile-optimized multi-sport coaching platform
 */

import React, { useState, useEffect } from 'react';
import Dashboard from './components/settings/Dashboard';
import DataManagement from './components/settings/DataManagement';
import Roster from './components/roster/Roster';
import PlayDesigner from './components/playbook/PlayDesigner';
import TeamSettings from './components/settings/TeamSettings';
import Scouting from './components/stats/Scouting';
import Stats from './components/stats/Stats';
import JerseyIcon from './components/common/JerseyIcon';
import LearnPage from './components/learn/LearnPage';
import { getSetting, setSetting } from './utils/db';

const APP_VERSION = '0.6.0';

const App = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [teamName, setTeamName] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth <= 1024);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth >= 768 && window.innerWidth <= 1024);
  const [jerseyTemplates, setJerseyTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState('default');
  const [refreshKey, setRefreshKey] = useState(0); // Force refresh Roster

  // Get the active jersey for nav icon - prefer isDefault, then activeTemplateId, then first
  const activeJersey = jerseyTemplates.find(t => t.isDefault) || 
                       jerseyTemplates.find(t => t.id === activeTemplateId) || 
                       jerseyTemplates[0] || 
                       { fillColor: '#2563eb', outlineColor: '#1e40af', numberColor: '#ffffff', numberOutline: '#1e40af' };

  // Migrate jersey template to include all required fields
  const migrateTemplate = (template) => ({
    // Start with all defaults
    id: 'default',
    name: 'Home',
    fillColor: '#2563eb',
    outlineColor: '#1e40af',
    numberColor: '#ffffff',
    numberOutline: '#1e40af',
    numberOutline2: null,
    textColor: '#ffffff',
    textOutline: '#1e40af',
    textOutline2: null,
    fontFamily: 'Impact, Haettenschweiler, sans-serif',
    logoUrl: null,
    numberY: 55,
    nameY: 5,
    numberSize: 100,
    nameSize: 100,
    jerseyBorderWeight: 12,
    numberBorderWeight: 6,
    numberBorderWeight2: 0,
    nameBorderWeight: 4,
    nameBorderWeight2: 0,
    // Override with saved values
    ...template
  });

  // Load team name and jerseys from IndexedDB
  useEffect(() => {
    const loadSettings = async () => {
      const savedTeamName = await getSetting('teamName', '');
      if (savedTeamName) setTeamName(savedTeamName);
      
      // Load all jersey templates and MIGRATE them
      const templates = await getSetting('jerseyTemplates', []);
      const activeId = await getSetting('activeJerseyTemplate', 'default');
      
      if (templates && templates.length > 0) {
        // Migrate all templates to ensure they have all required fields
        const migratedTemplates = templates.map(migrateTemplate);
        setJerseyTemplates(migratedTemplates);
        
        // Save migrated templates back to IndexedDB
        await setSetting('jerseyTemplates', migratedTemplates);
      }
      setActiveTemplateId(activeId);
    };
    loadSettings();
  }, []);

  // Reload everything when team settings closes
  // Refresh team data (jerseys, team name) - called when leaving Team Settings
  const refreshTeamData = async () => {
    // Refresh all templates (with migration)
    const templates = await getSetting('jerseyTemplates', []);
    const activeId = await getSetting('activeJerseyTemplate', 'default');
    
    if (templates && templates.length > 0) {
      const migratedTemplates = templates.map(migrateTemplate);
      setJerseyTemplates(migratedTemplates);
    }
    setActiveTemplateId(activeId);
    
    // Refresh team name
    const savedTeamName = await getSetting('teamName', '');
    if (savedTeamName) setTeamName(savedTeamName);
    
    // Force Roster to re-render with new templates
    setRefreshKey(prev => prev + 1);
  };

  // Save team name to IndexedDB
  useEffect(() => {
    if (teamName) {
      setSetting('teamName', teamName);
    }
  }, [teamName]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width <= 1024);
      // Auto-close menu on desktop
      if (width >= 768) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Auto-collapse sidebar on tablet when entering PlayDesigner
  useEffect(() => {
    if (isTablet && activeTab === 'plays') {
      setSidebarCollapsed(true);
    }
  }, [activeTab, isTablet]);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'roster', label: 'Team Roster', icon: 'jersey' },  // Special: renders JerseyIcon
    { id: 'plays', label: 'Playbook', icon: '📋' },
    { id: 'stats', label: 'Statistics', icon: '📈' },
    { id: 'scouting', label: 'Scouting', icon: '🔍' },
    { id: 'learn', label: 'Learn', icon: '🎓' },
    { id: 'teamSettings', label: 'Team Settings', icon: '⚙️' },  // Universal cog
    { id: 'data', label: 'Data Management', icon: '💾' }
  ];

  const handleNavClick = (tabId) => {
    // Refresh team data when leaving Team Settings tab
    if (activeTab === 'teamSettings' && tabId !== 'teamSettings') {
      refreshTeamData();
    }
    setActiveTab(tabId);
    setMenuOpen(false); // Close menu on mobile after selection
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onNavigate={handleNavClick} teamName={teamName} />;
      case 'roster':
        return <Roster 
          key={refreshKey}
          teamName={teamName} 
          jerseyTemplates={jerseyTemplates}
          activeTemplateId={activeTemplateId}
        />;
      case 'plays':
        return <PlayDesigner />;
      case 'stats':
        return <Stats onNavigate={handleNavClick} />;
      case 'scouting':
        return <Scouting onNavigate={handleNavClick} />;
      case 'learn':
        return <LearnPage onNavigate={handleNavClick} />;
      case 'data':
        return <DataManagement />;
      case 'teamSettings':
        return <TeamSettings />;
      default:
        return <Dashboard onNavigate={handleNavClick} teamName={teamName} />;
    }
  };

  return (
    <div data-element="app-root" className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile Header */}
      {isMobile && (
        <div data-element="app-mobile-header" className="fixed top-0 left-0 right-0 bg-gray-900 text-white z-50 flex items-center justify-between p-4 shadow-lg">
          <div data-element="app-mobile-header-logo" className="flex items-center space-x-2">
            <span className="text-2xl">🏆</span>
            <h1 className="text-lg font-bold">CoachPlaybook</h1>
          </div>
          <button
            data-element="app-mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      )}

      {/* Sidebar Navigation - Desktop always visible, Mobile overlay, Tablet collapsible */}
      <aside
        data-element="app-sidebar"
        className={`
          ${isMobile ? 'fixed inset-y-0 left-0 z-40' : 'relative'}
          ${isMobile && !menuOpen ? '-translate-x-full' : 'translate-x-0'}
          ${isTablet && sidebarCollapsed ? 'w-0 overflow-hidden opacity-0' : 'w-64'}
          bg-gray-900 text-white flex flex-col
          transition-all duration-300 ease-in-out
          ${isMobile ? 'mt-16' : ''}
        `}
        style={{ flexShrink: 0 }}
      >
        {/* Desktop Header (hidden on mobile) */}
        {!isMobile && (
          <div data-element="app-sidebar-header" className="p-6 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-3xl">🏀</span>
                <h1 className="text-xl font-bold">CoachPlaybook</h1>
              </div>
              {isTablet && !sidebarCollapsed && (
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  title="Hide sidebar"
                >
                  <span className="text-xl">✕</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Team Name */}
        <div data-element="app-sidebar-teamname" className="p-4 border-b border-gray-800">
          <label className="block text-xs text-gray-400 mb-2">Team Name</label>
          <input
            data-element="app-sidebar-teamname-input"
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="Enter team name..."
            className="w-full px-3 py-3 bg-gray-800 border border-gray-700 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Navigation Items */}
        <nav data-element="app-sidebar-nav" className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  data-element={`app-nav-${item.id}`}
                  onClick={() => handleNavClick(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-4 md:py-3 rounded-lg transition-colors text-left ${
                    activeTab === item.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  {item.icon === 'jersey' ? (
                    <div className="w-6 h-8 md:w-5 md:h-7 flex-shrink-0">
                      <JerseyIcon 
                        size={24}
                        number=""
                        view="front"
                        teamName=""
                        fillColor={activeJersey.fillColor}
                        outlineColor={activeJersey.outlineColor}
                        numberColor={activeJersey.numberColor}
                        numberOutline={activeJersey.numberOutline}
                        numberOutline2={activeJersey.numberOutline2}
                        jerseyBorderWeight={activeJersey.jerseyBorderWeight ?? 12}
                      />
                    </div>
                  ) : (
                    <span className="text-2xl md:text-xl">{item.icon}</span>
                  )}
                  <span className="font-medium text-base md:text-sm">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer with Version */}
        <div data-element="app-sidebar-footer" className="p-4 border-t border-gray-800">
          <div className="flex items-center justify-between">
            <span data-element="app-version" className="text-xs text-gray-500">v{APP_VERSION}</span>
            <span className="text-xs text-gray-500">Built for coaches</span>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {isMobile && menuOpen && (
        <div
          data-element="app-mobile-overlay"
          className="fixed inset-0 bg-black bg-opacity-50 z-30 mt-16"
          onClick={() => setMenuOpen(false)}
        />
      )}
      
      {/* Tablet Sidebar Toggle Button */}
      {isTablet && sidebarCollapsed && (
        <button
          data-element="app-tablet-sidebar-toggle"
          onClick={() => setSidebarCollapsed(false)}
          className="fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-r-lg shadow-lg transition-colors"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        >
          <span className="text-sm font-medium">☰ Menu</span>
        </button>
      )}

      {/* Main Content Area */}
      <main data-element="app-main-content" className={`overflow-y-auto overflow-x-hidden ${isMobile ? 'mt-16' : ''}`} style={{ flex: '1 1 0%', minWidth: 0, height: '100%' }}>
        {renderContent()}
      </main>
    </div>
  );
};

export default App;
